import { UUID } from "@opendaw/lib-std";
import { AssetPeerConnection } from "./AssetPeerConnection";
import * as ChunkProtocol from "./ChunkProtocol";
import { AssetZip } from "./AssetZip";
import { TrafficMeter } from "./TrafficMeter";
export class AssetServer {
    #signaling;
    #localPeerId;
    #assetReader;
    #trafficMeter;
    #connections = new Map();
    constructor(signaling, localPeerId, assetReader, trafficMeter) {
        this.#signaling = signaling;
        this.#localPeerId = localPeerId;
        this.#assetReader = assetReader;
        this.#trafficMeter = trafficMeter ?? new TrafficMeter();
        this.#signaling.subscribe(message => this.#onSignalingMessage(message));
        console.debug("[P2P:Server] initialized, peerId:", localPeerId);
    }
    #onSignalingMessage(message) {
        console.debug("[P2P:Server] received signaling message:", message.type, "from", message.peerId);
        switch (message.type) {
            case "asset-request":
                this.#onAssetRequest(message).catch(error => console.warn("[P2P:Server] error handling asset-request:", error));
                break;
            case "rtc-offer":
                this.#onRtcOffer(message).catch(error => console.warn("[P2P:Server] error handling rtc-offer:", error));
                break;
            case "rtc-ice-candidate":
                this.#onIceCandidate(message).catch(error => console.warn("[P2P:Server] error handling rtc-ice-candidate:", error));
                break;
        }
    }
    async #onAssetRequest(message) {
        const peerId = message.peerId;
        const assets = message.assets;
        console.debug("[P2P:Server] asset-request from", peerId, "for", assets.length, "assets");
        const have = [];
        for (const asset of assets) {
            try {
                const uuid = UUID.parse(asset.uuid);
                console.debug("[P2P:Server] checking", asset.assetType, asset.uuid);
                if (asset.assetType === "sample" && await this.#assetReader.hasSample(uuid)) {
                    console.debug("[P2P:Server] have sample", asset.uuid);
                    have.push(asset.uuid);
                }
                else if (asset.assetType === "soundfont" && await this.#assetReader.hasSoundfont(uuid)) {
                    console.debug("[P2P:Server] have soundfont", asset.uuid);
                    have.push(asset.uuid);
                }
                else if (asset.assetType === "cover" && await this.#assetReader.hasCover(uuid)) {
                    console.debug("[P2P:Server] have cover", asset.uuid);
                    have.push(asset.uuid);
                }
                else {
                    console.debug("[P2P:Server] do NOT have", asset.assetType, asset.uuid);
                }
            }
            catch (error) {
                console.warn("[P2P:Server] error checking asset", asset.uuid, error);
            }
        }
        if (have.length > 0) {
            console.debug("[P2P:Server] responding with inventory:", have, "to", peerId);
            this.#signaling.publish({
                type: "asset-inventory",
                peerId: this.#localPeerId,
                targetPeerId: peerId,
                have
            });
        }
        else {
            console.debug("[P2P:Server] no matching assets for", peerId);
        }
    }
    async #onRtcOffer(message) {
        const peerId = message.peerId;
        const targetPeerId = message.targetPeerId;
        if (targetPeerId !== this.#localPeerId) {
            console.debug("[P2P:Server] rtc-offer not for us (target:", targetPeerId, ")");
            return;
        }
        console.debug("[P2P:Server] handling rtc-offer from", peerId);
        const sdp = message.sdp;
        const connection = new AssetPeerConnection(this.#signaling, this.#localPeerId, peerId);
        this.#connections.set(peerId, connection);
        const channel = await connection.handleOffer(sdp);
        console.debug("[P2P:Server] data channel established with", peerId);
        channel.onmessage = (event) => {
            console.debug("[P2P:Server] data channel message received from", peerId);
            this.#onChannelMessage(connection, channel, event.data);
        };
    }
    async #onChannelMessage(connection, channel, data) {
        const request = JSON.parse(data);
        if (request.type !== "transfer-request") {
            console.debug("[P2P:Server] unknown channel message type:", request.type);
            return;
        }
        console.debug("[P2P:Server] transfer-request for", request.uuid, request.assetType);
        const uuid = UUID.parse(request.uuid);
        const assetType = request.assetType;
        let zipBytes;
        if (assetType === "sample") {
            console.debug("[P2P:Server] reading sample from OPFS...");
            const [wavBytes, meta] = await this.#assetReader.readSample(uuid);
            console.debug("[P2P:Server] sample read, wav size:", wavBytes.byteLength, "packing zip...");
            zipBytes = await AssetZip.packSample(wavBytes, meta);
        }
        else if (assetType === "soundfont") {
            console.debug("[P2P:Server] reading soundfont from OPFS...");
            const [sf2Bytes, meta] = await this.#assetReader.readSoundfont(uuid);
            console.debug("[P2P:Server] soundfont read, sf2 size:", sf2Bytes.byteLength, "packing zip...");
            zipBytes = await AssetZip.packSoundfont(sf2Bytes, meta);
        }
        else {
            console.debug("[P2P:Server] reading cover...");
            // The cover is already a compressed WebP and carries no metadata, so it is transferred as raw bytes.
            zipBytes = await this.#assetReader.readCover(uuid);
        }
        const chunks = ChunkProtocol.split(zipBytes);
        console.debug("[P2P:Server] sending", chunks.length, "chunks, zip size:", zipBytes.byteLength);
        const startPayload = new TextEncoder().encode(JSON.stringify({
            totalChunks: chunks.length,
            zipSize: zipBytes.byteLength
        }));
        if (!await connection.sendWithBackpressure(channel, ChunkProtocol.encode(ChunkProtocol.MsgType.TransferStart, uuid, 0, startPayload))) {
            console.debug("[P2P:Server] channel closed before transfer started for", request.uuid);
            return;
        }
        for (let index = 0; index < chunks.length; index++) {
            const encoded = ChunkProtocol.encode(ChunkProtocol.MsgType.ChunkData, uuid, index, chunks[index]);
            if (!await connection.sendWithBackpressure(channel, encoded)) {
                console.debug("[P2P:Server] channel closed at chunk", index, "of", chunks.length, "for", request.uuid);
                return;
            }
            this.#trafficMeter.recordUpload(encoded.byteLength);
        }
        if (!await connection.sendWithBackpressure(channel, ChunkProtocol.encode(ChunkProtocol.MsgType.TransferComplete, uuid, 0, new Uint8Array(0)))) {
            console.debug("[P2P:Server] channel closed before transfer-complete for", request.uuid);
            return;
        }
        console.debug("[P2P:Server] transfer complete for", request.uuid);
    }
    async #onIceCandidate(message) {
        const peerId = message.peerId;
        if (message.targetPeerId !== this.#localPeerId) {
            return;
        }
        const connection = this.#connections.get(peerId);
        if (connection === undefined) {
            return;
        }
        await connection.handleIceCandidate(message.candidate);
    }
    terminate() {
        console.debug("[P2P:Server] terminating");
        for (const connection of this.#connections.values()) {
            connection.terminate();
        }
        this.#connections.clear();
    }
}
